import React, { useState } from 'react'
import { keyframes } from '@emotion/react'
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded'
import { Box, Link, Typography } from '@mui/material'
import { useToasts } from 'react-toast-notifications'
import { useSessionUID } from 'src/session/useSession'
import { describeFailure } from '../../api/failureReason'
import FailureMessage from '../../components/FailureMessage'
import { EXAMPLE_PROVIDER, exampleProviderRole } from '../../tour/exampleFeed'
import { ExampleTag } from '../../tour/examples'
import { useTourExample } from '../../tour/tourExample'
import { advancedColors, surfaces } from '../../theme'
import { roleLabelFor } from '../careTeam/roleCategories'
import { RoleCategoryId } from '../careTeam/types'
import CareTeamAvatar from './CareTeamAvatar'
import { memberLabel, roleLine } from './roleLine'
import { CareTeamRosterEntry, CareTeamThreadSummary } from './types'
import { useCareTeamRoster } from './useCareTeamRoster'
import UnreadCount from './UnreadCount'
import { CareTeamThreadsState } from './useCareTeamThreads'

export type CareTeamChatSelection = {
  threadId: string | null
  title: string
  subtitle: string | null
  categoryId?: string | null
  // Set for a direct chat, so the list can show it once the viewer has written in it.
  thread?: CareTeamThreadSummary
}

type Props = { uid: string; threads: CareTeamThreadsState; onSelect: (selection: CareTeamChatSelection) => void }

const careCoordinationTicketUrl = (uid: string) =>
  'https://meru-internal.zendesk.com/hc/en-us/requests/new?ticket_form_id=19348691027611&tf_19496317570331=therapist' +
  `&tf_26611376511387=${encodeURIComponent(`${window.location.origin}/participant/${uid}`)}`

const wiggle = keyframes`
  0%, 60%, 100% { transform: rotate(0deg); }
  10%, 30%, 50% { transform: rotate(-14deg); }
  20%, 40% { transform: rotate(14deg); }
`

const otherParticipant = (thread: CareTeamThreadSummary, coachUid: string): string =>
  thread.participantAUid === coachUid ? thread.participantBUid : thread.participantAUid

// `member` is absent for someone who has since left the care team.
const selectionFor = (
  thread: CareTeamThreadSummary,
  name: string,
  member?: CareTeamRosterEntry
): CareTeamChatSelection => ({
  threadId: thread.threadId,
  thread,
  title: name,
  subtitle: member ? roleLine(member.name, member.role) : null,
  categoryId: member?.categoryId,
})

// Without `onClick` the row is inert, for the tour's example member.
const Row = ({
  avatar,
  title,
  subtitle,
  onClick,
  disabled,
  tag,
}: {
  avatar: React.ReactNode
  title: string
  subtitle: string | null
  onClick?: () => void
  disabled?: boolean
  tag?: React.ReactNode
}) => (
  <Box
    {...(onClick ? { component: 'button' as const, type: 'button', onClick, disabled } : {})}
    sx={{
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      px: 2,
      py: 1.25,
      border: 'none',
      backgroundColor: 'transparent',
      cursor: onClick ? 'pointer' : 'default',
      textAlign: 'left',
      font: 'inherit',
      ...(onClick && { '&:hover, &:focus-visible': { backgroundColor: surfaces.blue.bg, outline: 'none' } }),
    }}
  >
    {avatar}
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: surfaces.blue.fg, overflowWrap: 'anywhere' }}>
        {title}
      </Typography>
      <Typography variant="caption" component="div" sx={{ color: advancedColors.inkMuted, overflowWrap: 'anywhere' }}>
        {subtitle}
      </Typography>
    </Box>
    {tag}
  </Box>
)

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <Typography
    variant="caption"
    sx={{
      display: 'block',
      px: 2,
      pt: 2,
      pb: 0.5,
      fontWeight: 600,
      color: advancedColors.inkFaint,
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
    }}
  >
    {children}
  </Typography>
)

const CareTeamChatPicker = ({ uid, threads: { threads, groupUnreadCount, loading, startThread }, onSelect }: Props) => {
  const coachUid = useSessionUID()
  const { roster, nameFor } = useCareTeamRoster(uid, true)
  const tourExample = useTourExample()
  const [starting, setStarting] = useState(false)
  const { addToast } = useToasts()

  const otherMembers = roster.filter((member) => member.id !== coachUid)
  const memberFor = (id: string) => otherMembers.find((member) => member.id === id)

  const directChats = threads.map((thread) => ({ thread, otherId: otherParticipant(thread, coachUid) }))
  // Held until threads load, so an existing chat isn't listed as startable first.
  const startable = loading
    ? []
    : otherMembers.filter((member) => !directChats.some((chat) => chat.otherId === member.id))
  const showsExample =
    tourExample === 'care-team-member' && !loading && directChats.length === 0 && startable.length === 0
  const exampleRole = exampleProviderRole(
    (roster.find((member) => member.id === coachUid)?.categoryId as RoleCategoryId | undefined) ?? null
  )

  return (
    <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pb: 1 }}>
        <SectionLabel>Chats</SectionLabel>
        <Row
          avatar={<CareTeamAvatar group />}
          title="Full care team"
          subtitle={
            roster.length === 0
              ? 'No care team assigned yet.'
              : `${roster.length} member${roster.length === 1 ? '' : 's'}`
          }
          tag={<UnreadCount count={groupUnreadCount} />}
          onClick={() =>
            onSelect({
              threadId: null,
              title: 'Full care team',
              subtitle: roster.map(memberLabel).join(' · '),
            })
          }
        />
        {directChats.map(({ thread, otherId }) => {
          const member = memberFor(otherId)
          const name = member?.name ?? nameFor(otherId)
          return (
            <Row
              key={thread.threadId}
              avatar={<CareTeamAvatar name={name} categoryId={member?.categoryId} />}
              title={name}
              subtitle={member ? roleLine(member.name, member.role) : 'Direct message'}
              tag={<UnreadCount count={thread.unreadCount ?? 0} />}
              onClick={() => onSelect(selectionFor(thread, name, member))}
            />
          )
        })}
        {(startable.length > 0 || showsExample) && <SectionLabel>Start a new chat</SectionLabel>}
        {showsExample && (
          <Row
            avatar={<CareTeamAvatar name={EXAMPLE_PROVIDER.name} categoryId={exampleRole} />}
            title={EXAMPLE_PROVIDER.name}
            subtitle={roleLabelFor(exampleRole)}
            tag={<ExampleTag />}
          />
        )}
        {startable.map((member) => (
          <Row
            key={member.id}
            avatar={<CareTeamAvatar name={member.name} categoryId={member.categoryId} />}
            title={member.name}
            subtitle={roleLine(member.name, member.role)}
            disabled={starting}
            onClick={() => {
              setStarting(true)
              startThread(member.id)
                .then((thread) => onSelect(selectionFor(thread, member.name, member)))
                .catch((error) =>
                  addToast(<FailureMessage failure={describeFailure('Failed to start the chat.', error)} />, {
                    appearance: 'error',
                  })
                )
                .finally(() => setStarting(false))
            }}
          />
        ))}
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1.5,
          backgroundColor: surfaces.blue.bg,
          borderTop: `1px solid ${surfaces.blue.stroke}`,
        }}
      >
        <SupportAgentRoundedIcon
          sx={{
            color: surfaces.blue.fg,
            animation: `${wiggle} 1.2s ease-in-out 0.4s 2`,
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          }}
        />
        <Typography variant="caption" component="div" sx={{ color: advancedColors.inkMuted }}>
          Need to request help from care coordination?{' '}
          <Link href={careCoordinationTicketUrl(uid)} target="_blank" rel="noreferrer">
            Submit a ticket here!
          </Link>
        </Typography>
      </Box>
    </Box>
  )
}

export default CareTeamChatPicker
